/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {Grid, LinearProgress, Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import classnames from 'classnames';
import {
  cloneDeep,
  filter,
  isFunction,
  isNil
} from 'lodash';
import {WorkBook} from 'xlsx/types';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {
  Entity
} from '@ion/core/src/types/entities';
import {DropsheetOptions, TemplateColumn} from '@ion/core/src/types/templates';
import {getUniqueArray} from '@ion/core/src/utils/ArrayUtil';
import {isValidESN} from '@ion/core/src/utils/DeviceUtils';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {
  normalizeAttributes,
  normalizeHeaders,
  process
} from '@ion/core/src/xlsx/ExcelHelpers';
import {
  dropsheet
} from '@ion/core/src/xlsx/importer';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {
  FormItem
} from '@ion/dls/src/components/forms/FormItem';
import {
  FormSection
} from '@ion/dls/src/components/forms/FormSection';
import {
  FormTextField
} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {CancelButton} from '@ion/dls/src/editors/controls/CancelButton';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {FileIcon} from '@ion/dls/src/icons/FileIcon';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {TrashCanIcon} from '@ion/dls/src/icons/TrashCanIcon';
import {BulkImportTemplate} from '@ion/dls/src/imports/BulkImportTemplate';
import BulkUploadImporter from '@ion/dls/src/imports/BulkUploadImporter';
import ImportProgress from '@ion/dls/src/imports/ImportProgress';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {
  formatBytes,
  getESNs,
  MAX_DEVICES
} from './DevicePasswordUtils';
import {viewDevicePassword} from './state';

export interface DevicePasswordEditorProps {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  isCtcAdmin?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  uploadStatusContainer : {
    marginTop      : '20px',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  linearProgress : {
    maxWidth : '150px'
  },
  hideUploadStatusContainer : {
    display        : 'none',
    marginTop      : '20px',
    justifyContent : 'space-between'
  },
  fileInfoSection : {
    display : 'flex'
  },
  fileIcon : {
    display     : 'flex',
    marginRight : '12px'
  },
  fileSize : {
    opacity : '0.7'
  },
  input : {
    padding : '10px 0px 10px 11px'
  },
  underline : {
    backgroundColor : dlsColor('white'),
    '&:after'       : {
      borderColor : dlsColor('caBlue')
    },
    '&:before' : {
      borderColor       : `${dlsColor('grey5')} !important`,
      borderBottomWidth : '1px !important'
    }
  }
});

// defines the type definitions for import params ...
export type ImportParamsType = {
  fileName?: string;
  fileSize?: number;
  isComplete?: boolean;
  isFailure?: boolean,
  items?: Array<any>;
}

/**
 * The Device Password editor with set fields and encapsulated logic
 * for handling the form's state, including validation.
 *
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {boolean} isCtcAdmin: Return true/false
 */
export const DevicePasswordEditor = ({
  onCloseDialog,
  isCtcAdmin = false
}: DevicePasswordEditorProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();

  const dispatch                                  = useDispatch();
  const isMounted                                 = useHasMounted();
  const [identifierType, setIdentifierType]       = useState<string>('ESN');
  const [identifierValue, setIdentifierValue]     = useState<string>('');
  const [selectedDevices, setSelectedDevices]     = useState([]);
  const [isFormChanged, setIsFormChanged]         = useState<boolean>(false);
  const [inValidIdentifier, setInValidIdentifier] = useState<boolean>(false);
  const [maxDeviceError, setMaxDeviceError]       = useState<boolean>(false);
  const [isFileUploaded, setIsFileUploaded]       = useState<boolean>(false);
  const [importStatus, setImportStatus]           = useState<boolean>(false);
  const [importComplete, setImportComplete]       = useState<boolean>(false);
  const [importerOptions, setImporterOptions]     = useState<DropsheetOptions>();
  const [importParams, setImportParams]           = useState<ImportParamsType>();
  const templateColumns: TemplateColumn[]         = translate('admin-devices:devicePassword.dialog.template', {returnObjects: true});
  const attributes                                = idMap(templateColumns, 'header', 'key');
  const entityLabel                               = translate('admin-devices:devicePassword.dialog.title');
  const entity                                    = Entity.device;
  const id                                        = 'device-password-importer';

  const identifierTypeOptions = useMemo(() => ([
    {value: 'ESN', label: translate('admin-devices:view.additionalInfo.esn')},
    {value: 'ICCID', label: translate('admin-devices:view.additionalInfo.iccid')},
    {value: 'IMEI', label: translate('admin-devices:view.additionalInfo.imei')}
  ]), []);

  const validateIdentifier = useCallback((arr) => {
    let invalid = false;
    const bulkDevices = getUniqueArray(arr);
    setSelectedDevices(bulkDevices);
    setMaxDeviceError(bulkDevices?.length > MAX_DEVICES);
    bulkDevices?.forEach((esn) => {
      invalid = !isValidESN(esn);
    });
    return invalid;
  }, []);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    const deviceIdentifier = e?.target?.value;
    const devicesArr = deviceIdentifier?.split('\n');
    setInValidIdentifier(validateIdentifier(devicesArr));
    setIdentifierValue(deviceIdentifier?.replace(/\s+/g, '\n'));
    setIsFormChanged(true);
  }, []);

  const initializeImportParams = function() {
    setImportParams({
      items      : [],
      isComplete : false,
      isFailure  : false
    });
  };

  const clearImport = function() {
    const importContainer = document.getElementById('device-import') as HTMLInputElement;
    if (importContainer) {
      importContainer.value = null;
    }
    setImportStatus(false);
    initializeImportParams();
    setIsFileUploaded(false);
    setInValidIdentifier(false);
    setIdentifierValue('');
    setSelectedDevices([]);
  };

  const onImportCompleted = function(
    items: Array<any>,
    workbook: WorkBook,
    files: Array<any>
  ) {
    const file = files ? files[0] : {};
    let errorCheck = false;
    const mandatoryCols = filter(templateColumns, {required: true});
    for (const item of items) {
      for (const col of mandatoryCols) {
        if (isNil(item[col.key])) {
          errorCheck = true;
          break;
        }
      }
      if (errorCheck) break;
    }
    if (errorCheck) return onImportFailed();

    setImportParams({
      ...importParams,
      fileName   : file?.name,
      fileSize   : file?.size,
      isComplete : true,
      isFailure  : false,
      items
    });
    const deviceIds = getESNs(items);
    setInValidIdentifier(validateIdentifier(deviceIds));
    const identifiers = deviceIds.join('\n');
    setIdentifierValue(cloneDeep(identifiers));
  };

  const onImportFailed = function(e?: unknown) {
    clearImport();
    setImportParams({
      ...importParams,
      isComplete : false,
      isFailure  : true
    });
  };

  const converter = (column: string) => normalizeHeaders(column, normalizeAttributes(attributes));

  function handleImport(files: FileList) {
    setImportStatus(true);
    setIsFileUploaded(true);
    setInValidIdentifier(false);
    setIdentifierValue('');
    setSelectedDevices([]);
    setIsFormChanged(true);
    process(files, importerOptions);
  };

  const initializeImporterOptions = () => {
    setImporterOptions({
      onComplete  : onImportCompleted,
      onFailed    : onImportFailed,
      onSizeError : onImportFailed,
      parse       : true,
      converter
    });
  };

  useEffect(() => {
    if (!isMounted) {
      initializeImporterOptions();
      initializeImportParams();
    } else {
      importerOptions.target = document.getElementById(id);
      dropsheet(importerOptions, setImportStatus);
    }
  }, [isMounted]);

  useEffect(() => {
    setImportComplete(importStatus && importParams?.isComplete);
  }, [importStatus, importParams?.isComplete]);

  const length = importParams?.items?.length;
  const size   = isFunction(length?.toLocaleString) ? length?.toLocaleString() : length?.toString();

  const onSubmit = useCallback(() => {
    setIsFormChanged(true);
    if (identifierValue && !inValidIdentifier && !maxDeviceError) {
      dispatch(viewDevicePassword(selectedDevices, identifierType.toLocaleLowerCase()));
    }
  }, [selectedDevices]);

  const getFormErrorMessage = useCallback(() => {
    if (maxDeviceError) {
      return translate('admin-devices:devicePassword.dialog.maxDeviceError');
    }
    if (inValidIdentifier) {
      return translate('admin-devices:devicePassword.dialog.invalidIdentifier', {identifier: identifierType});
    }
    if (isFormChanged) {
      return translate('admin-devices:view.bulkSciSetting.identifierError');
    }
    return '';
  }, [maxDeviceError, inValidIdentifier, isFormChanged]);

  return (
    <Dialog open
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCloseDialog} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('admin-devices:devicePassword.dialog.title')}
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div className={classes.formWrapper}>
          <DialogContent className={classes.formDialogContent}>
            <FormSection>
              <FormItem>
                <Dropdown displaySelectedLabel
                  options={identifierTypeOptions}
                  fieldLabel={translate('admin-devices:devicePassword.dialog.deviceIdentifierType')}
                  value={identifierType}
                  name={'deviceIdentifierType'}
                  onChange={function(event, {value}) {
                    setIdentifierType(value);
                  }} />
              </FormItem>
              <FormItem fullWidth>
                <FormTextField disabled={isFileUploaded}
                  fieldLabel={identifierType}
                  placeholder={translate('admin-devices:devicePassword.dialog.placeholderLabel', {identifier: identifierType})}
                  name={'deviceIdentifier'}
                  onChange={handleFieldChange}
                  hasError={isFormChanged &&
                    (identifierValue ? (inValidIdentifier || maxDeviceError) : !isFileUploaded)}
                  errorText={getFormErrorMessage()}
                  minRows={1}
                  maxRows={5}
                  multiline
                  value={identifierValue}
                  InputProps={{
                    classes : {
                      input     : classes.input,
                      underline : classes.underline
                    }
                  }} />
              </FormItem>
              <FormItem fullWidth>
                <BulkUploadImporter id={id}
                  size={size}
                  uploadTitle={translate('admin-devices:devicePassword.dialog.uploadDevices')}
                  onImport={handleImport}
                  entity={entity}
                  entityLabel={entityLabel}
                  importParams={importParams} />
                <BulkImportTemplate ignoreEntity
                  omitDownloadEntityTemplate
                  entity={entity}
                  entityLabel={entityLabel}
                  templateColumns={templateColumns}
                  templateOptions={{}}
                  onDownload={null} />
                <div id='upload-status' className={classnames(importStatus
                  ? classes.uploadStatusContainer : classes.hideUploadStatusContainer)}>
                  <div className={classes.fileInfoSection}>
                    <div className={classes.fileIcon}><FileIcon /></div>
                    <div>
                      <ImportProgress isCompleted={importComplete}
                        text={importParams?.fileName || ''} />
                    </div>
                  </div>
                  <div className={classes.fileSize}>
                    <ImportProgress isCompleted={importComplete}
                      text={formatBytes(importParams?.fileSize) || ''} />
                  </div>
                  <div className={classes.linearProgress}>
                    <Grid xs item>
                      {importStatus ? importParams.isComplete ? translate('common:editors.import.uploadCompleted') : translate('common:editors.import.uploadProgress') : ''}
                    </Grid>
                    <Grid xs item>
                      {importStatus ? importParams.isComplete ? <LinearProgress variant='determinate' value={100} /> : <LinearProgress /> : ''}
                    </Grid>
                  </div>
                  <IconButton style={{padding: '0px'}} onClick={clearImport} icon={<TrashCanIcon />} />
                </div>
              </FormItem>
            </FormSection>
          </DialogContent>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        <CancelButton onCancel={onCloseDialog} />
        <SaveButton onSave={onSubmit} />
      </DialogActions>
    </Dialog>
  );
};

export default DevicePasswordEditor;
